import React, { useEffect, useRef, useState } from "react";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";
import CategoryAPI from "../../api/categoryApi";
import AsyncSelect from "react-select/async";
import slugify from "slugify";
import SEOFields from "../../components/partials/SEOFields";
import ImageUploader from "../../components/partials/ImageUploader";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import SunEditor from "suneditor-react";

function AddNew() {
  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [categoryTree, setCategoryTree] = useState([]);
  const [visibleOptions, setVisibleOptions] = useState([]);
  const [selectedPath, setSelectedPath] = useState([]);
  const [finalCategory, setFinalCategory] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const editorRef = useRef(null);

  const INITIAL_FORM_DATA = {
    name: "",
    description: "",
    shortDescription: "",
    slug: "",
    image: "",
    parentId: "",
    navbar: false,
    homepage: false,
  };

  const INITAL_SEO_DATA = {
    metaTitle: "",
    metaDescription: "",
    tags: [],
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [seoData, setSeoData] = useState(INITAL_SEO_DATA);

  useEffect(() => {
    CategoryAPI.getListforDropdown().then((res) => {
      if (res.code === 200) {
        setCategoryTree(res.data);
        setVisibleOptions(res.data); // level 0 only
      }
    });
  }, []);

  const handleSelect = (item) => {
    const newPath = [...selectedPath, item];
    setSelectedPath(newPath);

    if (item.children && item.children.length > 0) {
      // Go deeper
      setVisibleOptions(item.children);
      setFormData((prev) => ({
        ...prev,
        parentId: item._id,
      }));
    } else {
      // Leaf selected
      setFinalCategory(item);
      setFormData((prev) => ({
        ...prev,
        parentId: item._id,
      }));
      setIsOpen(false);
    }
  };

  const selectedLabel = selectedPath.map((i) => i.name).join(" → ");

  const flattenCategories = (categories, level = 0, parentPath = "") => {
    let result = [];

    categories.forEach((cat) => {
      const fullPath = parentPath ? `${parentPath} → ${cat.name}` : cat.name;

      result.push({
        label: fullPath,
        value: cat._id,
        slug: cat.slugPath,
        level,
      });

      if (cat.children && cat.children.length > 0) {
        result = result.concat(
          flattenCategories(cat.children, level + 1, fullPath),
        );
      }
    });

    return result;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      description: content,
    }));
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name field is mandatory";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    showLoader();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const payload = {
        ...formData,
        slug: slugify(formData.name, { lower: true }),
        metaTitle: seoData.metaTitle,
        metaDescription: seoData.metaDescription,
        keywords: seoData.tags.join(","),
      };

      const res = await CategoryAPI.addDetails(payload);

      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITIAL_FORM_DATA);

        setSeoData({
          metaTitle: "",
          metaDescription: "",
          tags: [],
        });
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Add a New Category</h4>
            <h6>
              Create a new category to engage customers and drive store
              visibility.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Create New Category</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="Category Image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="categories"
              />

              <div className="row mt-3">
                {categoryTree.length > 0 && (
                  <div className="col-lg-6 col-sm-12">
                    <div className="mb-3">
                      <label className="form-label">Parent Category</label>
                      <div className="input-group">
                        <span className="input-group-text" id="basic-addon1">
                          <i className="fa fa-home"></i>
                        </span>

                        <div
                          className="position-relative"
                          style={{ width: "100%" }}
                        >
                          <div
                            className="form-control"
                            onClick={() => setIsOpen(!isOpen)}
                            style={{ cursor: "pointer" }}
                          >
                            {selectedLabel || "Select Category"}
                          </div>

                          {isOpen && (
                            <div
                              className="border bg-white shadow position-absolute w-100"
                              style={{ zIndex: 10 }}
                            >
                              {visibleOptions.map((item) => (
                                <div
                                  key={item._id}
                                  onClick={() => handleSelect(item)}
                                  style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #eee",
                                  }}
                                >
                                  {item.name}
                                  {item.children?.length > 0 && " ›"}
                                </div>
                              ))}

                              {selectedPath.length > 0 && (
                                <div
                                  onClick={() => {
                                    const parentPath = selectedPath.slice(
                                      0,
                                      -1,
                                    );
                                    setSelectedPath(parentPath);
                                    const parent =
                                      parentPath.length === 0
                                        ? categoryTree
                                        : parentPath[parentPath.length - 1]
                                            .children;
                                    setVisibleOptions(parent);
                                  }}
                                  className="p-2 text-primary"
                                  style={{ cursor: "pointer" }}
                                >
                                  ← Back
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {errors.selectedStore && (
                        <span className="errors text-danger">
                          {errors.selectedStore}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Category Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-pencil"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.name && (
                      <span className="errors text-danger">{errors.name}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">Show In Navbar</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="navbar"
                      role="switch"
                      id="flexSwitchCheckChecked"
                      checked={formData.navbar}
                      onChange={handleChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexSwitchCheckChecked"
                    >
                      Show
                    </label>
                  </div>
                </div>
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">Show in Home Page</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="homepage"
                      role="switch"
                      id="flexSwitchCheckChecked"
                      checked={formData.homepage}
                      onChange={handleChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexSwitchCheckChecked"
                    >
                      Show
                    </label>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <label className="form-label">Short Description</label>
                  <textarea
                    name="shortDescription"
                    className="form-control"
                    value={formData.shortDescription}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-12">
                  <label className="form-label">Description</label>

                  <SunEditor
                    getSunEditorInstance={(sunEditor) => {
                      editorRef.current = sunEditor;
                    }}
                    height="250px"
                    setOptions={{
                      buttonList: [
                        ["undo", "redo"],
                        ["font", "fontSize"],
                        ["formatBlock"],
                        ["bold", "underline", "italic", "strike"],
                        ["fontColor", "hiliteColor"],
                        ["align", "list", "table"],
                        ["link", "image", "video"],
                        ["fullScreen", "preview", "print"],
                        ["codeView"],
                      ],
                      allowedClassNames: ".*", // sab classes allow
                      attributesWhitelist: {
                        all: "style|class|id",
                      },
                      addTagsWhitelist: ".*", // extra tags remove na kare
                      imageFileInput: true,
                      font: [
                        "Arial",
                        "Comic Sans MS",
                        "Courier New",
                        "Georgia",
                        "Tahoma",
                        "Trebuchet MS",
                        "Verdana",
                      ],
                      fontSize: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36], // include 12px
                    }}
                    setContents={formData.description}
                    onChange={handleEditorChange}
                  />
                </div>
              </div>

              <hr />

              <SEOFields
                seoData={seoData}
                setSeoData={setSeoData}
                slug={slugify(formData.name || "", { lower: true })}
                errors={errors}
              />

              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  {!isLoading && (
                    <button
                      type="submit"
                      id="submitBtn"
                      className="btn btn-primary shadow-none"
                    >
                      Submit Details
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNew;
